import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import DataTablePagination from '../../components/Table/DataTablePagination';
import SearchField from '../../components/Table/SearchField';
import RegistrationDecisionDialog from '../../components/Registrations/RegistrationDecisionDialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useListPage } from '../../hooks/useListPage';
import { toast } from 'react-toastify';

const fontBase = { fontFamily: 'Roboto, Arial, sans-serif' };

const RegistrationApprovals = () => {
  const navigate = useNavigate();

  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // A new search restarts at the first page.
  const [page, setPage] = useListPage(JSON.stringify([debouncedSearch, rowsPerPage]));
  const [total, setTotal] = useState(0);
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRegistrations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const params = { page, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await api.get('/users/pending-registrations', { params });
      setRegistrations(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Both this page and the review page go through the same dialog, so a
  // rejection carries a reason wherever it is recorded from. The reason reaches
  // the supplier verbatim in the notification the approval route sends.
  const submitDecision = async ({ status, comment, department }) => {
    if (!decision) return;
    try {
      setSubmitting(true);
      await api.put(`/users/${decision.registration._id}/supplier-approval`, {
        status,
        comment,
        ...(department ? { department } : {}),
      });
      toast.success(`Registration ${status} successfully`);
      setDecision(null);
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update registration');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const openDecision = (registration, status, event) => {
    event.stopPropagation();
    setDecision({ registration, status });
  };

  const headerCellSx = { ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 };
  const bodyCellSx = { ...fontBase, fontSize: '14px', color: '#111827', py: 1.5 };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ ...fontBase, fontWeight: 'bold', mb: 1, color: '#111827', fontSize: { xs: '22px', sm: '28px' } }}>
            Registration Approvals
          </Typography>
          <Typography sx={{ ...fontBase, color: '#6b7280', fontSize: '14px', mb: 3 }}>
            Review and approve or reject new supplier registration requests. Select a row to see the full submission.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search name, email, company, reg. no."
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={headerCellSx}>Name</TableCell>
                  <TableCell sx={headerCellSx}>Company Name</TableCell>
                  <TableCell sx={{ ...headerCellSx, display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                  <TableCell sx={{ ...headerCellSx, display: { xs: 'none', md: 'table-cell' } }}>Registered</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ ...fontBase, color: '#6b7280', fontSize: '13px' }}>
                        {debouncedSearch ? 'No registrations match that search' : 'No pending registration requests'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.map((reg) => (
                    <TableRow
                      key={reg._id}
                      hover
                      onClick={() => navigate(`/registrations/${reg._id}`)}
                      sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>
                        {[reg.firstName, reg.lastName].filter(Boolean).join(' ') || '-'}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {reg.companyName || <Typography component="span" sx={{ color: '#9ca3af', fontSize: '13px' }}>Not provided</Typography>}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', sm: 'table-cell' } }}>
                        {reg.email}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatDate(reg.createdAt)}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {/* A supplier cannot be onboarded on an address nobody
                            has proved they can read, and the server refuses the
                            approval outright — so say why here rather than
                            letting the admin click into an error. */}
                        {reg.isEmailVerified === false ? (
                          <Chip
                            label="Email not verified"
                            size="small"
                            sx={{ backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: 500, fontSize: '13px', height: '24px', borderRadius: '12px', '& .MuiChip-label': { padding: '0 8px' } }}
                          />
                        ) : (
                          <Chip
                            label="Pending Approval"
                            size="small"
                            sx={{ backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: 500, fontSize: '13px', height: '24px', borderRadius: '12px', '& .MuiChip-label': { padding: '0 8px' } }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => { e.stopPropagation(); navigate(`/registrations/${reg._id}`); }}
                            sx={{ textTransform: 'none', fontSize: '12px', minWidth: '48px', color: '#374151' }}
                          >
                            View
                          </Button>
                          <Tooltip title={reg.isEmailVerified === false ? 'This supplier must verify their email address before they can be onboarded' : ''}>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={reg.isEmailVerified === false}
                                onClick={(e) => openDecision(reg, 'approved', e)}
                                sx={{ textTransform: 'none', fontSize: '12px', minWidth: '72px', bgcolor: '#578A18', '&:hover': { bgcolor: '#467014' }, '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}
                              >
                                Approve
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => openDecision(reg, 'rejected', e)}
                            sx={{ textTransform: 'none', fontSize: '12px', minWidth: '62px', borderColor: '#dc2626', color: '#dc2626', '&:hover': { borderColor: '#b91c1c', color: '#b91c1c', backgroundColor: '#fef2f2' } }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              page={page}
              total={total}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              loading={loading}
            />
          </TableContainer>
        )}
      </Container>

      <RegistrationDecisionDialog
        open={Boolean(decision)}
        status={decision?.status}
        supplierName={
          decision?.registration?.companyName ||
          [decision?.registration?.firstName, decision?.registration?.lastName].filter(Boolean).join(' ')
        }
        submitting={submitting}
        onCancel={() => setDecision(null)}
        onConfirm={submitDecision}
      />

      <Footer />
    </Box>
  );
};

export default RegistrationApprovals;
